<template>
  <div class="ticker-flex" :class="(strategy || '').toLowerCase()">
    <span class="sr-only" v-if="accessibilityFeatures">{{ orderTicker }}</span>
    <v-button
      v-if="showToggleIcon"
      :style="{ top: getTotalRowHeight() }"
      class="order-details-collapse"
      @click="toggleOrderDetail"
      @keydown.enter.prevent="toggleOrderDetail"
      :aria-expanded="expanded ? 'true' : 'false'"
      ref="expandButton"
    >
      <font-awesome-icon :icon="['fas', expanded ? 'minus' : 'plus']"></font-awesome-icon
    ></v-button>
    <template v-if="firstChild && isConditionalOrder">
      <div class="ticker-flex" :class="{ 'left-margin': orderDetail }">
        <img :src="stgIcon" class="stgIcon" />
        <div
          aria-hidden="true"
          :class="{
            'ticker-root-class': true,
            'last-child': lastChild
          }"
        >
          <v-button class="btn btn-secondary ticker-button text-left" ref="tickerButton" @focus="onFocus"
            ><span v-if="accessibilityFeatures" class="sr-only">{{ params.data.allTickerArray.join(', ') }}</span
            >{{ ticker }}</v-button
          >
          <c-halt-indicator :tradeHaltValue="tradeHaltValue" :toolTipRequired="false" />
        </div>
      </div>
    </template>
    <template v-else>
      <div
        aria-hidden="true"
        :class="{
          'ticker-root-class': firstChild,
          'ticker-child-class': !firstChild,
          'left-margin-child': orderDetail && isConditionalOrder,
          'last-child': lastChild,
          'no-details': !orderDetail
        }"
      >
        <v-button class="btn btn-secondary ticker-button text-left" ref="tickerButton" @focus="onFocus"
          ><span v-if="accessibilityFeatures" class="sr-only">{{ params.data.allTickerArray.join(', ') }}</span
          >{{ ticker }}</v-button
        >
        <c-halt-indicator :tradeHaltValue="tradeHaltValue" :toolTipRequired="false" />
      </div>
      <span class="sr-only" v-if="accessibilityFeatures">{{ orderTicker }}</span>
    </template>
  </div>
</template>

<script>
import CHaltIndicator from '@custom/CHaltIndicator';
import { library as fontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { faPlus, faMinus } from '@fortawesome/pro-solid-svg-icons';
fontAwesomeLibrary.add(faPlus, faMinus);

export default {
  name: 'ticker-renderer',
  components: {
    CHaltIndicator
  },
  data() {
    return {
      stgIcon: null,
      expanded: false
    };
  },
  computed: {
    ticker() {
      return this.params.data.security.displayTicker;
    },
    strategy() {
      return this.params.data.strategy;
    },
    firstChild() {
      return this.params.data.index === 0;
    },
    isConditionalOrder() {
      return ['OTA', 'OCO', 'FTO', 'MLO'].includes(this.strategy);
    },
    orderNodes() {
      let nodes = [];
      this.params.api.forEachNode(node => {
        if (node.data && node.data.orderKey === this.params.data.orderKey) {
          nodes.push(node);
        }
      });
      return nodes;
    },
    lastChild() {
      return this.params.data.index === this.orderNodes.length - 1;
    },
    orderDetail() {
      return this.params.orderDetail;
    },
    showToggleIcon() {
      return this.lastChild && this.orderDetail && this.params.data.security
        ? ['EQUITY', 'OPTION'].includes(this.params.data.security.type)
        : false;
    },
    tradeHaltValue() {
      return this.params.tradeHaltValue;
    },
    accessibilityFeatures() {
      return this.$store.state.applicationSettings.accessibility.settings.accessibilityFeatures;
    },
    orderTicker() {
      // to support screen readers and keyboard navigation
      if (this.orderNodes && this.orderNodes.length) {
        let tickerParts = [this.orderNodes[0].data.security.displayTicker];
        if (this.orderNodes.length > 1 && this.orderNodes[1]) {
          tickerParts = [
            this.$t('white-label.renderers.' + this.strategy.toLowerCase()),
            this.$t('white-label.renderers.firstLeg'),
            ...tickerParts,
            this.$t('white-label.renderers.secondLeg'),
            this.orderNodes[1].data ? this.orderNodes[1].data.security.displayTicker : ''
          ];
          if (this.orderNodes.length === 3) {
            tickerParts.push(
              this.$t('white-label.renderers.thirdLeg'),
              this.orderNodes[2].data ? this.orderNodes[2].data.security.displayTicker : ''
            );
          }
        }
        if (this.orderDetail) {
          tickerParts.push(this.$t(`white-label.renderers.pressEnterTo${this.expanded ? 'Close' : 'Open'}Details`));
        }
        return tickerParts.join('. ') + '.';
      }
    }
  },
  methods: {
    refresh(params) {
      this.params = params;
      return true;
    },
    toggleOrderDetail() {
      if (this.params.context.toggleOrderDetail(this.params)) this.expanded = !this.expanded;
    },
    getTotalRowHeight() {
      let rh = this.params.node.rowHeight;
      if (['MLO', 'OCO', 'OTA'].includes(this.strategy)) return -(rh / 2) + 'px';
      if (this.strategy === 'FTO') return -rh + 'px';
      return '0px';
    },
    onFocus() {
      this.params.context.setIsTickerFocused(['EQUITY', 'OPTION'].includes(this.params.data.security.type), true);
    },
    onKeyDown(e) {
      if (e.key === 'Tab') {
        if (e.target.classList.contains('order-details-collapse') && !e.shiftKey) {
          let focusedCell = this.params.api.getFocusedCell();
          this.params.eGridCell
            .closest('.widget-body')
            .querySelector(`[row-index="${focusedCell.rowIndex}"] .ticker-button`)
            .focus();
          e.preventDefault();
        } else {
          this.params.context.setIsTickerFocused(false);
        }
      }
      if (e.key === 'ArrowRight') {
        this.params.context.setIsTickerFocused(false);
      }
    }
  },
  created() {
    this.expanded = this.orderNodes[this.orderNodes.length - 1].expanded;
    this.params.eGridCell.addEventListener('keydown', this.onKeyDown);
  },
  beforeDestroy() {
    this.params.eGridCell.removeEventListener('keydown', this.onKeyDown);
  },
  mounted() {
    if (this.isConditionalOrder) {
      import(`@/assets/conditional-orders/${this.strategy.toUpperCase()}.svg`).then(icon => {
        this.stgIcon = icon.default;
      });
    }
  }
};
</script>

<style lang="scss">
.ticker-flex {
  display: flex;
  flex-flow: row nowrap;

  &.spl {
    align-items: center;
  }

  .order-details-collapse,
  .ticker-button {
    position: relative;
    z-index: 1;
    margin-right: -5px;
    cursor: pointer;
    margin-top: 0px !important;
    border: transparent;
    background: transparent;
  }

  .btn-secondary,
  .btn-secondary.active,
  .btn-secondary:active,
  .btn-secondary.hover,
  .btn-secondary:hover {
    border: none !important;
    background: none !important;
    box-shadow: none !important;
    outline-style: none !important;
    transition: none !important;
  }

  .btn-secondary.focus,
  .btn-secondary:focus {
    border: 1px solid !important;
    border-color: get-color('cyan', 'lighten-1') !important;
  }
}

.arrow-class {
  position: relative;
  z-index: 1;
  height: 45px;
}

.ticker-child-class {
  padding-left: 25px;

  &.last-child {
    padding-left: 10px;
  }

  &.last-child.no-details {
    padding-left: 15px;
  }
}
.ticker-root-class {
  padding-left: 5px;
  z-index: 1;
}
.left-margin {
  margin-left: 6px;
  align-items: flex-start;
  padding-left: 20px;
}
.left-margin-child {
  margin-left: 19px;
}
.ota .stgIcon {
  width: 32px;
  height: 41px;
  z-index: 1;
}
.oco .stgIcon {
  width: 32px;
  height: 39px;
  z-index: 1;
  position: relative;
  left: -9px;
}
.fto .stgIcon {
  width: 32px;
  height: 62px;
  z-index: 1;
}
.mlo .stgIcon {
  height: 36px;
  width: 13px;
  z-index: 1;
  margin: 4px 0px 0px -4px;
}
.mlo .ticker-child-class {
  padding-left: 5px;
}
.ota .ticker-root-class {
  left: -22px;
  position: relative;
}
.ota .ticker-child-class {
  position: relative;
  left: 16px;
  top: 1px;
}
.ota .last-child {
  &.no-details {
    padding-left: 25px;
  }
}
.oco .ticker-root-class {
  left: -18px;
  top: -2px;
  position: relative;
}
.oco .ticker-child-class {
  position: relative;
  left: -1px;
  top: 3px;
}
.oco .last-child {
  &.no-details {
    padding-left: 20px;
  }
}
.fto .ticker-root-class {
  position: relative;
  left: -22px;
  top: -3px;
}
.fto .ticker-child-class {
  position: relative;
  left: 17px;
}
.fto .last-child {
  top: 3px;

  &.no-details {
    padding-left: 25px;
  }
}
</style>
